import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import {
  formatCOPShort, formatRelative, getScoreBadge,
  getUrgencyColor, formatDate, getAIPriorityBadge,
} from "@/utils/formatting";
import { TEST_IDS } from "@/constants/testIds";
import {
  Flame, TrendingUp, Calendar, DollarSign, RefreshCw,
  ChevronRight, Clock, AlertCircle, Sparkles, Brain, CheckCircle2,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [aiRecs, setAiRecs] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("priorities");

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/today"),
      api.get("/dashboard/recent-alerts"),
    ])
      .then(([s, t, a]) => {
        setStats(s.data);
        setPriorities(t.data.priorities || []);
        setAiRecs(t.data.ai_recommendations || []);
        setRecentAlerts(a.data || []);
        // Switch to AI tab if there are recommendations and no priorities
        if ((t.data.ai_recommendations || []).length > 0 && !(t.data.priorities || []).length) {
          setActiveTab("ai");
        }
      })
      .catch(() => setError("Error cargando datos"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <DashboardSkeleton />;

  const hasAiData = stats?.ai_alta_prioridad > 0 || stats?.ai_revisar > 0;
  const isEmpty = !priorities.length && !stats?.total_active;

  return (
    <div data-testid={TEST_IDS.DASHBOARD} className="p-4 lg:p-6 space-y-5 max-w-7xl mx-auto">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-slate-50 tracking-tight">
          ¿En qué debería trabajar hoy?
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Bienvenido, {user?.name}.{" "}
          {stats?.last_sync ? `Última sync: ${formatDate(stats.last_sync)}` : "Sin sincronización aún."}
        </p>
      </div>

      {/* Stats row 1: Traditional */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-in-up">
          <StatCard
            testId={TEST_IDS.STAT_TOTAL}
            icon={<TrendingUp className="w-5 h-5 text-blue-400" />}
            label="Procesos Activos"
            value={stats.total_active?.toLocaleString("es-CO")}
            color="blue"
          />
          <StatCard
            testId={TEST_IDS.STAT_HIGH}
            icon={<Flame className="w-5 h-5 text-red-400" />}
            label="Score Alto (70+)"
            value={stats.high_score}
            color="red"
          />
          <StatCard
            testId={TEST_IDS.STAT_CLOSING}
            icon={<Clock className="w-5 h-5 text-amber-400" />}
            label="Cierran Esta Semana"
            value={stats.closing_this_week}
            color="amber"
          />
          <StatCard
            testId={TEST_IDS.STAT_VALUE}
            icon={<DollarSign className="w-5 h-5 text-emerald-400" />}
            label="Valor Potencial"
            value={formatCOPShort(stats.total_value)}
            color="emerald"
            isText
          />
        </div>
      )}

      {/* Stats row 2: AI */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 animate-fade-in-up">
          <StatCard
            testId="ai-stat-alta"
            icon={<Brain className="w-5 h-5 text-emerald-400" />}
            label="IA: Alta Prioridad"
            value={stats.ai_alta_prioridad ?? "—"}
            color="emerald"
            badge="IA"
          />
          <StatCard
            testId="ai-stat-revisar"
            icon={<Sparkles className="w-5 h-5 text-amber-400" />}
            label="IA: Revisar"
            value={stats.ai_revisar ?? "—"}
            color="amber"
            badge="IA"
          />
          <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400 text-xs font-medium uppercase tracking-wide">Cobertura IA</span>
            </div>
            {(stats.ai_analyzed_relevant ?? stats.ai_analyzed) > 0 ? (
              <>
                <p className="font-outfit font-bold text-xl text-slate-50">
                  {(stats.ai_analyzed_relevant ?? stats.ai_analyzed)?.toLocaleString("es-CO")}
                  {" / "}
                  {(stats.relevant_total ?? stats.total_active)?.toLocaleString("es-CO")}
                </p>
                <p className="text-slate-500 text-xs mt-0.5">
                  procesos de tu nicho analizados
                </p>
                <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, (((stats.ai_analyzed_relevant ?? stats.ai_analyzed) || 0) / ((stats.relevant_total ?? stats.total_active) || 1)) * 100)}%`
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="text-slate-500 text-xs">Sin análisis todavía</p>
            )}
          </div>
        </div>
      )}

      {/* Main content grid */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Priority / AI Tabs */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex gap-1 bg-slate-900 border border-slate-700/50 rounded-lg p-1">
              <button
                onClick={() => setActiveTab("priorities")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  activeTab === "priorities"
                    ? "bg-slate-700 text-slate-100"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                Prioridades
              </button>
              <button
                data-testid="ai-recs-tab"
                onClick={() => setActiveTab("ai")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                  activeTab === "ai"
                    ? "bg-emerald-600/30 text-emerald-300 border border-emerald-500/20"
                    : "text-slate-500 hover:text-slate-300"
                }`}
              >
                <Brain className="w-3 h-3" />
                IA Recomienda
                {aiRecs.length > 0 && (
                  <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 rounded-full">
                    {aiRecs.length}
                  </span>
                )}
              </button>
            </div>
            <Link
              to={activeTab === "ai" ? "/radar?ai_priority=ALTA_PRIORIDAD" : "/radar"}
              className="text-emerald-400 text-xs hover:text-emerald-300 flex items-center gap-1"
            >
              Ver todos <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {activeTab === "priorities" && (
            <div data-testid={TEST_IDS.PRIORITY_LIST} className="space-y-2">
              {isEmpty ? (
                <EmptyState />
              ) : priorities.length === 0 ? (
                <div className="bg-slate-900 border border-slate-700/30 rounded-xl p-6 text-center">
                  <p className="text-slate-500 text-sm">Sin procesos de alta prioridad</p>
                  <p className="text-slate-600 text-xs mt-1">Realiza una sincronización para actualizar datos</p>
                </div>
              ) : (
                priorities.map((p) => <PriorityCard key={p.id} process={p} />)
              )}
            </div>
          )}

          {activeTab === "ai" && (
            <div data-testid="ai-recs-list" className="space-y-2">
              {aiRecs.length === 0 ? (
                <AiEmptyState />
              ) : (
                aiRecs.map((p) => <AiRecommendationCard key={p.id} process={p} />)
              )}
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <SyncAnalysisPanel stats={stats} onAnalysisDone={loadData} isAdmin={user?.role === "ADMIN"} />

          {/* Recent alerts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-outfit font-semibold text-slate-200 text-sm">Alertas Recientes</h3>
              <Link to="/alertas" className="text-emerald-400 text-xs hover:text-emerald-300">Ver todas</Link>
            </div>
            <div className="space-y-2">
              {recentAlerts.length === 0 ? (
                <p className="text-slate-500 text-xs py-3 text-center">Sin alertas recientes</p>
              ) : (
                recentAlerts.slice(0, 4).map((a) => <AlertRow key={a.id} alert={a} />)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ testId, icon, label, value, color, isText = false, badge }) {
  const colorMap = {
    blue: "bg-blue-500/10 border-blue-500/20",
    red: "bg-red-500/10 border-red-500/20",
    amber: "bg-amber-500/10 border-amber-500/20",
    emerald: "bg-emerald-500/10 border-emerald-500/20",
  };
  return (
    <div data-testid={testId} className={`${colorMap[color]} border rounded-xl p-4 relative`}>
      {badge && (
        <span className="absolute top-2 right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-400 font-mono">
          {badge}
        </span>
      )}
      <div className="flex items-center justify-between mb-2">{icon}</div>
      <p className="font-outfit font-bold text-xl text-slate-50">{value ?? "—"}</p>
      <p className="text-slate-400 text-xs mt-0.5">{label}</p>
    </div>
  );
}

function PriorityCard({ process }) {
  const badge = getScoreBadge(process.score_category, process.score);
  const urgencyColor = getUrgencyColor(process.closing_date);
  const aiBadge = process.ai_priority ? getAIPriorityBadge(process.ai_priority) : null;
  return (
    <Link
      to={`/procesos/${process.id}`}
      data-testid={TEST_IDS.PRIORITY_ITEM}
      className="block bg-slate-900 border border-slate-700/50 rounded-xl p-4 card-hover group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>
              {badge.label}
            </span>
            {aiBadge && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${aiBadge.cls}`}>
                IA: {aiBadge.label}
              </span>
            )}
            <span className="font-mono text-slate-500 text-[10px]">{process.score}/100</span>
          </div>
          <p className="text-slate-200 text-sm font-medium line-clamp-2 group-hover:text-emerald-300 transition-colors">
            {process.object || "Proceso sin descripción"}
          </p>
          <p className="text-slate-500 text-xs mt-1 truncate">{process.entity}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-slate-300 text-xs font-mono">{formatCOPShort(process.value)}</p>
          <p className={`text-xs mt-1 font-medium ${urgencyColor}`}>{formatRelative(process.closing_date)}</p>
        </div>
      </div>
    </Link>
  );
}

function AiRecommendationCard({ process }) {
  const urgencyColor = getUrgencyColor(process.closing_date);
  const aiBadge = getAIPriorityBadge(process.ai_priority);
  return (
    <Link
      to={`/procesos/${process.id}`}
      data-testid="ai-rec-item"
      className="block bg-slate-900 border border-emerald-500/15 rounded-xl p-4 card-hover group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {aiBadge && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${aiBadge.cls}`}>
                {aiBadge.label}
              </span>
            )}
            <span className="font-mono text-emerald-400 text-[10px]">IA {process.ai_score}/100</span>
          </div>
          <p className="text-slate-200 text-sm font-medium line-clamp-2 group-hover:text-emerald-300 transition-colors">
            {process.object || "Proceso sin descripción"}
          </p>
          {process.ai_summary && (
            <p className="text-slate-500 text-xs mt-1 line-clamp-2">{process.ai_summary}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-slate-300 text-xs font-mono">{formatCOPShort(process.value)}</p>
          <p className={`text-xs mt-1 font-medium ${urgencyColor}`}>{formatRelative(process.closing_date)}</p>
        </div>
      </div>
    </Link>
  );
}

function SyncAnalysisPanel({ stats, onAnalysisDone, isAdmin }) {
  const [syncing, setSyncing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeMsg, setAnalyzeMsg] = useState("");

  const handleSync = async () => {
    setSyncing(true);
    try { await api.post("/sync/trigger"); } catch (_) {}
    setTimeout(() => { setSyncing(false); }, 3000);
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalyzeMsg("");
    try {
      const r = await api.post("/analysis/batch", null, { params: { limit: 50 } });
      setAnalyzeMsg(r.data.message || "Análisis iniciado");
      setTimeout(() => { setAnalyzeMsg(""); onAnalysisDone(); }, 5000);
    } catch (e) {
      setAnalyzeMsg("Error al iniciar análisis");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div data-testid={TEST_IDS.LAST_SYNC} className="bg-slate-900 border border-slate-700/50 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-outfit font-semibold text-slate-200 text-sm">SECOP II & Análisis IA</h3>
        <span className={`w-2 h-2 rounded-full ${stats?.sync_status === "SUCCESS" ? "bg-emerald-400" : "bg-amber-400"}`} />
      </div>
      <p className="text-slate-500 text-xs">
        {stats?.last_sync ? `Sync: ${formatDate(stats.last_sync)}` : "Sin sincronización previa"}
      </p>

      <button
        data-testid={TEST_IDS.SYNC_TRIGGER}
        onClick={handleSync}
        disabled={syncing}
        className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300 text-xs py-2 rounded-lg transition-colors disabled:opacity-50"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
        {syncing ? "Sincronizando..." : "Sincronizar SECOP II"}
      </button>

      {isAdmin && (
        <button
          data-testid="ai-analyze-btn"
          onClick={handleAnalyze}
          disabled={analyzing}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/30 text-emerald-400 text-xs py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <Brain className={`w-3.5 h-3.5 ${analyzing ? "animate-pulse" : ""}`} />
          {analyzing ? "Analizando..." : "Analizar con IA (50 procesos)"}
        </button>
      )}

      {analyzeMsg && (
        <p className="text-emerald-400 text-[10px] text-center animate-fade-in-up">{analyzeMsg}</p>
      )}
    </div>
  );
}

function AlertRow({ alert }) {
  const typeIcon = {
    NEW_PROCESS: "N",
    CHANGE: "C",
    CLOSING_SOON: "T",
    FAVORITE_CHANGE: "F",
    AI_RECOMMENDATION: "IA",
  };
  const typeColor = {
    NEW_PROCESS: "bg-blue-500/20 text-blue-400",
    CHANGE: "bg-amber-500/20 text-amber-400",
    CLOSING_SOON: "bg-red-500/20 text-red-400",
    FAVORITE_CHANGE: "bg-amber-500/20 text-amber-400",
    AI_RECOMMENDATION: "bg-emerald-500/20 text-emerald-400",
  };
  return (
    <div className={`bg-slate-800/50 rounded-lg p-2.5 ${!alert.is_read ? "border-l-2 border-emerald-500" : ""}`}>
      <div className="flex items-start gap-2">
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono shrink-0 mt-0.5 ${typeColor[alert.type] || "bg-slate-700 text-slate-400"}`}>
          {typeIcon[alert.type] || "•"}
        </span>
        <p className="text-slate-300 text-xs line-clamp-2">{alert.title}</p>
      </div>
      <p className="text-slate-600 text-[10px] mt-0.5 pl-7">{formatRelative(alert.created_at)}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-slate-900 border border-slate-700/50 border-dashed rounded-xl p-8 text-center">
      <AlertCircle className="w-8 h-8 text-slate-600 mx-auto mb-3" />
      <p className="text-slate-400 text-sm font-medium">Sin procesos cargados</p>
      <p className="text-slate-600 text-xs mt-1">
        Ejecuta la primera sincronización con SECOP II para ver procesos relevantes.
      </p>
    </div>
  );
}

function AiEmptyState() {
  return (
    <div className="bg-slate-900 border border-slate-700/50 border-dashed rounded-xl p-8 text-center">
      <Brain className="w-8 h-8 text-slate-600 mx-auto mb-3" />
      <p className="text-slate-400 text-sm font-medium">Sin recomendaciones IA</p>
      <p className="text-slate-600 text-xs mt-1">
        Usa el botón "Analizar con IA" para clasificar los procesos automáticamente.
      </p>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto animate-pulse">
      <div className="h-8 bg-slate-800 rounded w-64" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-slate-800 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-800 rounded-xl" />)}
      </div>
      <div className="h-64 bg-slate-800 rounded-xl" />
    </div>
  );
}
